import writeXlsxFile from "write-excel-file";
import { dataFormat } from "../../../shared/functions/Directives";
import { fullPerspectiveName } from "../../../shared/interfaces/IPerspectiveTabs";
import { IMeasureCompany } from "../../../shared/models/MeasureCompany";
import { IObjective } from "../../../shared/models/Objective";
import { rateColor } from "./Scorecard";
import { ICompanyAgreementTableRowItem } from "../../../shared/functions/scorecard-pdf/CompanyPerformanceAgreementDocument ";


//#region Interfaces and Constants
interface ExcelCell {
  value: any;
  type?: StringConstructor | NumberConstructor | DateConstructor;
  format?: string;
  rowSpan?: number;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  align?: "left" | "center" | "right";
  alignVertical?: "top" | "center" | "bottom";
  borderColor?: string;
  wrap?: boolean;
  fontWeight?: "bold" | "normal";
}

interface SchemaColumn {
  column: string;
  type: StringConstructor | NumberConstructor | DateConstructor;
  color?: string;
  backgroundColor?: string;
  fontWeight?: "bold" | "normal";
  width: number;
  fontSize?: number;
  align?: "left" | "center" | "right";
  alignVertical?: "top" | "center" | "bottom";
  wrap?: boolean;
  valueGetter: (row: ICompanyAgreementTableRowItem) => any;
  backgroundColorGetter?: (rating: number, hasValue?: boolean) => string;
  format?: string;
  dynamicType?: boolean;
  mergeGroup?: string[];
}

type RowGroup = {
  perspective: string;
  objectiveId: string;
  rows: ICompanyAgreementTableRowItem[];
};

const PERSPECTIVE_ORDER = [
  "Financial Sustainability",
  "Stakeholder Value Addition",
  "Operational Excellence & Governance",
  "Human Capital & Transformation",
];

const createSchemaColumn = (
  column: string,
  valueGetter: SchemaColumn["valueGetter"],
  config: Partial<SchemaColumn>
): SchemaColumn => ({
  column,
  valueGetter,
  type: String,
  fontWeight: "normal",
  alignVertical: "top",
  wrap: true,
  width: 20,
  backgroundColor: "#ffffff",
  ...config,
});

const COMPANY_EXCEL_SCHEMA = [
  createSchemaColumn(
    "Perspectives",
    (row) => row.perspective,
    {
      fontWeight: "bold",
      width: 20,
      mergeGroup: ["perspective"],
    }
  ),
  createSchemaColumn(
    "Strategic Objectives",
    (row) => row.description,
    {
      mergeGroup: ["description"],
      fontWeight: "bold",
      width: 40
    }
  ),
  createSchemaColumn(
    "Weight",
    (row) => row.weight,
    {
      mergeGroup: ["description"], // Same merge group as description
      type: Number,
      format: "#,##0",
      fontWeight: "bold",
      width: 10
    }
  ),
  createSchemaColumn(
    "Measures/KPI",
    (row) => row.measure,
    {
      fontWeight: "bold",
      width: 25,
    }
  ),
  createSchemaColumn(
    "Baseline",
    (row) => row.baseline,
    {
      width: 15,
      dynamicType: true,
    }
  ),
  createSchemaColumn(
    "Quarterly Target, Q1",
    (row) => row.q1target,
    {
      width: 15,
      dynamicType: true,
    }
  ),
  createSchemaColumn(
    "Quarterly Target, Q2",
    (row) => row.q2target,
    {
      width: 15,
      dynamicType: true,
    }
  ),
  createSchemaColumn(
    "Quarterly Target, Q3",
    (row) => row.q3target,
    {
      width: 15,
      dynamicType: true,
    }
  ),
  createSchemaColumn(
    "Quarterly Target, Q4",
    (row) => row.q4target,
    {
      width: 15,
      dynamicType: true,
    }
  ),
  createSchemaColumn(
    "Annual Target",
    (row) => row.annualTarget,
    {
      width: 15,
      dynamicType: true,
    }
  ),
  createSchemaColumn(
    "Rating Scale 1-5",
    (row) => row.ratingScale,
    {
      width: 20,
    }
  ),
  createSchemaColumn(
    "Key Initiatives",
    (row) => row.keyInitiatives,
    {
      width: 40,
    }
  ),
  createSchemaColumn(
    "Target Date",
    (row) => row.targetDate ? new Date(row.targetDate) : null,
    {
      type: Date,
      width: 15,
      format: "dd mmmm yyyy",
    }
  ),
  createSchemaColumn(
    "Comments",
    (row) => row.comments,
    {
      width: 40,
    }
  ),
  createSchemaColumn(
    "Source of Evidence",
    (row) => row.sourceOfEvidence,
    {
      width: 25,
    }
  ),
  createSchemaColumn(
    "Midterm E Rating",
    (row) => row.rating,
    {
      type: Number,
      width: 15,
      align: "center",
      alignVertical: "center",
      format: "#,##0",
      fontSize: 16,
      fontWeight: "bold",
      color: "#ffffff",
      backgroundColorGetter: (rating, hasValue) => rateColor(rating, hasValue, true),
    }
  ),
  createSchemaColumn(
    "Midterm S Rating",
    (row) => row.q2supervisorRating,
    {
      type: Number,
      width: 15,
      align: "center",
      alignVertical: "center",
      format: "#,##0",
      fontSize: 16,
      fontWeight: "bold",
      color: "#ffffff",
      backgroundColorGetter: (rating, hasValue) => rateColor(rating, hasValue, true),
    }
  ),
  createSchemaColumn(
    "Midterm F Rating",
    (row) => row.q2FinalRating,
    {
      type: Number,
      width: 15,
      align: "center",
      alignVertical: "center",
      format: "#,##0",
      fontSize: 16,
      fontWeight: "bold",
      color: "#ffffff",
      backgroundColorGetter: (rating, hasValue) => rateColor(rating, hasValue, true),
    }
  ),
  createSchemaColumn(
    "Assessment E Rating",
    (row) => row.q4rating,
    {
      type: Number,
      width: 15,
      align: "center",
      alignVertical: "center",
      format: "#,##0",
      fontSize: 16,
      fontWeight: "bold",
      color: "#ffffff",
      backgroundColorGetter: (rating, hasValue) => rateColor(rating, hasValue, true),
    }
  ),
  createSchemaColumn(
    "Assessment S Rating",
    (row) => row.q4supervisorRating,
    {
      type: Number,
      width: 15,
      align: "center",
      alignVertical: "center",
      format: "#,##0",
      fontSize: 16,
      fontWeight: "bold",
      color: "#ffffff",
      backgroundColorGetter: (rating, hasValue) => rateColor(rating, hasValue, true),
    }
  ),
  createSchemaColumn(
    "Assessment F Rating",
    (row) => row.q4FinalRating,
    {
      type: Number,
      width: 15,
      align: "center",
      alignVertical: "center",
      format: "#,##0",
      fontSize: 16,
      fontWeight: "bold",
      color: "#ffffff",
      backgroundColorGetter: (rating, hasValue) => rateColor(rating, hasValue, true),
    }
  ),
];

const COMPANY_COLUMN_WIDTHS = COMPANY_EXCEL_SCHEMA.map(({ width }) => ({ width }));
//#endregion

//#region Data Preparation
const createRatingScale = (measure: IMeasureCompany) => {
  const ratings = [1, 2, 3, 4, 5].map(n => ({
    value: measure[`rating${n}` as keyof IMeasureCompany],
    label: `${n} = ${dataFormat(
      measure.dataType,
      measure[`rating${n}` as keyof IMeasureCompany] as  number | null | string,
      measure.dataSymbol
    )}`
  }));

  return ratings.filter(r => r.value).map(r => r.label).join("\n");
};

const mapMeasureToRow = (objectives: IObjective[], measure: IMeasureCompany) => {
  const strategic = objectives.find(o => o.id === measure.objective);
  
  return {
    perspective: fullPerspectiveName(strategic?.perspective ?? "-"),
    objectiveId: strategic?.id ?? "unknown",
    description: strategic?.description ?? "-",
    weight: strategic?.weight ?? 0,
    measure: measure.description ?? "-",
    baseline: measure.baseline ?? "-",
    q1target: measure.quarter1Target ?? "-",
    q2target: measure.quarter2Target ?? "-",
    q3target: measure.quarter3Target ?? "-",
    q4target: measure.quarter4Target ?? "-",
    annualTarget: measure.annualTarget ?? "-",
    ratingScale: createRatingScale(measure),
    keyInitiatives: measure.activities ?? null,
    targetDate: measure.targetDate ?? null,
    sourceOfEvidence: measure.sourceOfEvidence ?? null,
    comments: measure.comments ?? null,
    dataType: measure.dataType,
    dataSymbol: measure.dataSymbol,
    rating: measure.rating ?? null,
    q2supervisorRating: measure.q2supervisorRating ?? null,
    q2FinalRating: measure.q2FinalRating ?? null,
    q4rating: measure.q4rating ?? null,
    q4supervisorRating: measure.q4supervisorRating ?? null,
    q4FinalRating: measure.q4FinalRating ?? null,
  } as ICompanyAgreementTableRowItem;
};

const prepareCompanyData = (
  objectives: IObjective[],
  measures: IMeasureCompany[]
) => {
  return measures
    .map(measure => mapMeasureToRow(objectives, measure))
    .sort((a, b) => {
      const perspectiveCompare = 
        PERSPECTIVE_ORDER.indexOf(a.perspective) - 
        PERSPECTIVE_ORDER.indexOf(b.perspective);
      
      return perspectiveCompare !== 0 
        ? perspectiveCompare 
        : a.objectiveId.localeCompare(b.objectiveId);
    });
};
//#endregion

//#region Excel Formatting
const createBaseCell = (
  column: SchemaColumn,
  value: any,
  rowSpan?: number
): ExcelCell => ({
  value,
  type: column.type,
  format: column.format,
  rowSpan,
  fontSize: column.fontSize,
  color: column.color,
  backgroundColor: column.backgroundColor,
  align: column.align,
  alignVertical: column.alignVertical,
  borderColor: "#555555",
  wrap: column.wrap,
  fontWeight: column.fontWeight,
});

const determineCellTypeAndFormat = (
  cell: ExcelCell,
  column: SchemaColumn,
  row: ICompanyAgreementTableRowItem
) => {
  if (column.dynamicType && row.dataType) {
    const numericTypes = ["Currency", "Number", "Custom"];
    
    if (numericTypes.includes(row.dataType)) {
      cell.type = Number;
      cell.format = row.dataType === "Custom" 
        ? `#,##0${row.dataSymbol || ""}` 
        : "#,##0.00";
      cell.value = isNaN(cell.value) ? null : Number(cell.value);
    } else if (row.dataType === "Percentage") {
      cell.type = Number;
      cell.format = "0.0%";
      cell.value = isNaN(cell.value) ? null : Number(cell.value) / 100;
    } else if (row.dataType === "Date") {
      cell.type = Date;
      cell.format = "dd mmmm yyyy";
      cell.value = cell.value ? new Date(cell.value) : null;
    } else {
      cell.type = String;
      cell.value = cell.value?.toString() ?? "-";
    }
  }
  return cell;
};

const createFormattedCell = (
  column: SchemaColumn,
  row: ICompanyAgreementTableRowItem,
  rowSpan?: number
) => {
  const rawValue = column.valueGetter(row);
  const hasValue = rawValue !== null && rawValue !== undefined;
  
  const cell = createBaseCell(column, rawValue, rowSpan);
  cell.backgroundColor = column.backgroundColorGetter?.(rawValue, hasValue) 
    ?? column.backgroundColor;

  return determineCellTypeAndFormat(cell, column, row);
};

const groupRows = (rows: ICompanyAgreementTableRowItem[]) => {
  const groups: RowGroup[] = [];
  let currentGroup: RowGroup | null = null;

  for (const row of rows) {
    if (!currentGroup || 
        row.perspective !== currentGroup.perspective || 
        row.objectiveId !== currentGroup.objectiveId) {
      currentGroup = {
        perspective: row.perspective,
        objectiveId: row.objectiveId,
        rows: []
      };
      groups.push(currentGroup);
    }
    currentGroup.rows.push(row);
  }

  return groups;
};



const formatExcelData = (rows: ICompanyAgreementTableRowItem[]) => {
  const excelData: ExcelCell[][] = [COMPANY_EXCEL_SCHEMA.map(col => ({
    value: col.column,
    fontWeight: "bold",
    fontSize: 12,
    backgroundColor: col.backgroundColor,
    wrap: col.wrap,
  }))];

  const perspectiveGroups = new Map<string, {
    totalRows: number;
    objectives: Map<string, {
      description: string;
      weight: number;
      measures: ICompanyAgreementTableRowItem[];
    }>;
  }>();

  // 1. Group data hierarchy
  rows.forEach(row => {
    if (!perspectiveGroups.has(row.perspective)) {
      perspectiveGroups.set(row.perspective, {
        totalRows: 0,
        objectives: new Map()
      });
    }
    const perspective = perspectiveGroups.get(row.perspective)!;
    
    if (!perspective.objectives.has(row.objectiveId)) {
      perspective.objectives.set(row.objectiveId, {
        description: row.description,
        weight: row.weight,
        measures: []
      });
    }
    const objective = perspective.objectives.get(row.objectiveId)!;
    
    objective.measures.push(row);
    perspective.totalRows++;
  });

  // 2. Generate Excel rows
  perspectiveGroups.forEach((perspective, perspectiveName) => {
    let perspectiveRowsAdded = 0;

    perspective.objectives.forEach((objective, objectiveId) => {
      const measureRows = objective.measures;
      const objectiveRowSpan = measureRows.length;

      measureRows.forEach((measureRow, measureIndex) => {
        const excelRow: (ExcelCell | null)[] = [];
        const isFirstMeasureRow = measureIndex === 0;

        COMPANY_EXCEL_SCHEMA.forEach(col => {
          // Perspective column
          if (col.mergeGroup?.includes("perspective")) {
            excelRow.push(
              perspectiveRowsAdded === 0
                ? createFormattedCell(col, measureRow, perspective.totalRows)
                : null
            );
            return;
          }

          // Objective description & weight columns
          if (col.mergeGroup?.includes("description")) {
            excelRow.push(
              isFirstMeasureRow
                ? createFormattedCell(col, { 
                    ...measureRow,
                    description: objective.description,
                    weight: objective.weight
                  }, objectiveRowSpan)
                : null
            );
            return;
          }

          // Regular cells
          excelRow.push(createFormattedCell(col, measureRow));
        });

        excelData.push(excelRow as ExcelCell[]);
        perspectiveRowsAdded++;
      });
    });
  });

  return excelData;
};
//#endregion


export const exportCompanyScorecardExcel = async (
  title: string,
  objectives: IObjective[],
  measures: IMeasureCompany[]
) => {
  try {
    const data = formatExcelData(prepareCompanyData(objectives, measures));
    
    await writeXlsxFile(data as any, {
      columns: COMPANY_COLUMN_WIDTHS,
      fileName: `${title}.xlsx`,
      sheet: "Company Scorecard",
      fontFamily: "Candara",
      fontSize: 12,
    });

    return true;
  } catch (error) {
    console.error("Excel export failed:", error);
    return false;
  }
};